/**
 * Utilities for game report packages and artifacts returned by the analytics API.
 * Frontend treats artifact `url` as the render target; it does not reconstruct storage paths.
 */

const ARTIFACT_TYPE_LABELS = {
  game_report: "Overview",
  exec_summary: "Exec Summary",
  behavioral_analysis: "Behavioral Analysis",
  evidence_workbook: "Evidence Workbook",
  genre_benchmark: "Genre Benchmark",
};

const VIEWER_TAB_ORDER = [
  "game_report",
  "exec_summary",
  "behavioral_analysis",
  "evidence_workbook",
  "genre_benchmark",
];

/**
 * @param {string|undefined|null} urlValue
 * @returns {string|null}
 */
export function getSafeReportUrl(urlValue) {
  if (typeof urlValue !== "string") return null;

  const trimmedValue = urlValue.trim();
  if (!trimmedValue || trimmedValue === "null" || trimmedValue === "undefined") {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedValue);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return null;
    }
    return parsedUrl.toString();
  } catch (e) {
    return null;
  }
}

/**
 * @param {object} artifact
 * @returns {string}
 */
export function getArtifactDisplayTitle(artifact) {
  if (artifact?.title) return artifact.title;
  if (artifact?.artifactType && ARTIFACT_TYPE_LABELS[artifact.artifactType]) {
    return ARTIFACT_TYPE_LABELS[artifact.artifactType];
  }
  return artifact?.artifactType || "Report";
}

/**
 * @param {object} pkg
 * @returns {string}
 */
export function getPackageId(pkg) {
  return String(pkg?._id ?? pkg?.id ?? "");
}

/**
 * @param {object} pkg
 * @returns {boolean}
 */
export function isReadyPackage(pkg) {
  return String(pkg?.status || "").toLowerCase() === "ready";
}

/**
 * @param {object} pkg
 * @returns {boolean}
 */
export function isLatestPackage(pkg) {
  return pkg?.isLatest === 1 || pkg?.isLatest === true;
}

/**
 * @param {object|null|undefined} analyticsData
 * @returns {object[]}
 */
export function getReportPackages(analyticsData) {
  if (!analyticsData) return [];

  if (Array.isArray(analyticsData.reportPackages) && analyticsData.reportPackages.length > 0) {
    return [...analyticsData.reportPackages].sort((a, b) => {
      if (isLatestPackage(a) && !isLatestPackage(b)) return -1;
      if (!isLatestPackage(a) && isLatestPackage(b)) return 1;
      const nameA = String(a.name || "");
      const nameB = String(b.name || "");
      return nameB.localeCompare(nameA);
    });
  }

  if (analyticsData.reportPackage) {
    return [analyticsData.reportPackage];
  }

  return [];
}

/**
 * @param {object[]} packages
 * @returns {string|null}
 */
export function getDefaultPackageId(packages) {
  if (!packages?.length) return null;

  const readyPackages = packages.filter(isReadyPackage);
  const pool = readyPackages.length > 0 ? readyPackages : packages;
  const latestPackage = pool.find(isLatestPackage);

  if (latestPackage) {
    return getPackageId(latestPackage);
  }

  return getPackageId(pool[0]);
}

/**
 * @param {object} pkg
 * @returns {string}
 */
export function getPackageDisplayLabel(pkg) {
  const name = pkg?.name || "Report package";
  const buildSuffix = pkg?.buildId ? ` — Build ${pkg.buildId}` : "";
  const latestSuffix = isLatestPackage(pkg) ? " (Latest)" : "";
  return `${name}${buildSuffix}${latestSuffix}`;
}

/**
 * @param {object[]} artifacts
 * @param {string|null|undefined} packageId
 * @returns {object[]}
 */
export function filterArtifactsForPackage(artifacts, packageId) {
  if (!packageId || !Array.isArray(artifacts)) {
    return artifacts || [];
  }

  const normalizedPackageId = String(packageId);
  const hasPackageIds = artifacts.some(
    (artifact) => artifact.packageId != null || artifact.package_id != null
  );

  if (!hasPackageIds) {
    return artifacts;
  }

  return artifacts.filter((artifact) => {
    const artifactPackageId = String(artifact.packageId ?? artifact.package_id ?? "");
    return artifactPackageId === normalizedPackageId;
  });
}

function sortArtifacts(artifacts) {
  return [...artifacts].sort((a, b) => {
    const orderA = Number(a.sortOrder ?? 0);
    const orderB = Number(b.sortOrder ?? 0);
    return orderA - orderB;
  });
}

function isPrimaryArtifact(artifact) {
  return artifact?.isPrimary === 1 || artifact?.isPrimary === true;
}

function getFileType(artifact) {
  return String(artifact?.fileType || "").toLowerCase();
}

function isHtmlArtifact(artifact) {
  return getFileType(artifact) === "html";
}

function isJsonArtifact(artifact) {
  return getFileType(artifact) === "json";
}

function mapRenderableArtifacts(artifacts) {
  return artifacts
    .filter((artifact) => isHtmlArtifact(artifact) || isJsonArtifact(artifact))
    .map((artifact) => ({
      ...artifact,
      id: String(artifact._id ?? artifact.id ?? ""),
      displayTitle: getArtifactDisplayTitle(artifact),
      safeUrl: getSafeReportUrl(artifact.url),
      fileType: getFileType(artifact),
    }));
}

function getArtifactTypeKey(artifact) {
  const artifactType = String(artifact?.artifactType || "").toLowerCase();
  if (artifactType === "genre_benchmark_report") return "genre_benchmark";
  return artifactType;
}

function buildViewerTab({
  id,
  artifactType,
  displayTitle,
  renderMode,
  viewKind,
  safeUrl,
  jsonArtifactId = null,
  htmlArtifactId = null,
  htmlFallbackUrl = null,
  fileType,
  isPrimary = false,
  sortOrder = 0,
}) {
  return {
    id: String(id),
    artifactType,
    displayTitle,
    renderMode,
    viewKind,
    safeUrl,
    jsonArtifactId: jsonArtifactId ? String(jsonArtifactId) : null,
    htmlArtifactId: htmlArtifactId ? String(htmlArtifactId) : null,
    htmlFallbackUrl: htmlFallbackUrl || null,
    fileType,
    isPrimary,
    sortOrder,
  };
}

/**
 * One tab per report type. JSON is preferred over HTML when both exist.
 * Exec Summary has no JSON artifact, so it reuses the game-report JSON brief when available.
 *
 * @param {object[]} mappedArtifacts
 * @returns {object[]}
 */
export function buildViewerTabs(mappedArtifacts) {
  const withUrl = (mappedArtifacts || []).filter((artifact) => artifact.safeUrl);
  const usedIds = new Set();
  const tabs = [];

  VIEWER_TAB_ORDER.forEach((artifactType) => {
    const ofType = withUrl.filter((artifact) => getArtifactTypeKey(artifact) === artifactType);
    const json = ofType.find(isJsonArtifact);
    const html = ofType.find(isHtmlArtifact);

    if (artifactType === "exec_summary" && !json) {
      const gameJson = withUrl.find(
        (artifact) => isJsonArtifact(artifact) && getArtifactTypeKey(artifact) === "game_report"
      );
      if (gameJson) {
        tabs.push(
          buildViewerTab({
            id: `exec-from-${gameJson.id}`,
            artifactType: "exec_summary",
            displayTitle: ARTIFACT_TYPE_LABELS.exec_summary,
            renderMode: "json",
            viewKind: "exec_summary",
            safeUrl: gameJson.safeUrl,
            jsonArtifactId: gameJson.id,
            htmlArtifactId: html?.id || null,
            htmlFallbackUrl: html?.safeUrl || null,
            fileType: "json",
            isPrimary: Boolean(html && isPrimaryArtifact(html)),
            sortOrder: html?.sortOrder ?? gameJson.sortOrder,
          })
        );
        if (html?.id) usedIds.add(html.id);
        return;
      }
    }

    if (json) {
      tabs.push(
        buildViewerTab({
          id: json.id,
          artifactType,
          displayTitle: ARTIFACT_TYPE_LABELS[artifactType] || json.displayTitle,
          renderMode: "json",
          viewKind: artifactType,
          safeUrl: json.safeUrl,
          jsonArtifactId: json.id,
          htmlArtifactId: html?.id || null,
          htmlFallbackUrl: html?.safeUrl || null,
          fileType: "json",
          isPrimary: isPrimaryArtifact(json) || (html ? isPrimaryArtifact(html) : false),
          sortOrder: json.sortOrder,
        })
      );
      usedIds.add(json.id);
      if (html?.id) usedIds.add(html.id);
      return;
    }

    if (html) {
      tabs.push(
        buildViewerTab({
          id: html.id,
          artifactType,
          displayTitle: ARTIFACT_TYPE_LABELS[artifactType] || html.displayTitle,
          renderMode: "iframe",
          viewKind: artifactType,
          safeUrl: html.safeUrl,
          htmlArtifactId: html.id,
          htmlFallbackUrl: html.safeUrl,
          fileType: "html",
          isPrimary: isPrimaryArtifact(html),
          sortOrder: html.sortOrder,
        })
      );
      usedIds.add(html.id);
    }
  });

  withUrl.forEach((artifact) => {
    if (usedIds.has(artifact.id) || !isHtmlArtifact(artifact)) return;
    if (getArtifactTypeKey(artifact) === "evidence_appendix") return;
    if (VIEWER_TAB_ORDER.includes(getArtifactTypeKey(artifact))) return;

    tabs.push(
      buildViewerTab({
        id: artifact.id,
        artifactType: artifact.artifactType,
        displayTitle: artifact.displayTitle,
        renderMode: "iframe",
        viewKind: getArtifactTypeKey(artifact),
        safeUrl: artifact.safeUrl,
        htmlArtifactId: artifact.id,
        htmlFallbackUrl: artifact.safeUrl,
        fileType: "html",
        isPrimary: isPrimaryArtifact(artifact),
        sortOrder: artifact.sortOrder,
      })
    );
  });

  return tabs;
}

function getArtifactSearchText(artifact) {
  return [
    artifact?.artifactType,
    artifact?.title,
    artifact?.name,
    artifact?.fileName,
    artifact?.filename,
    artifact?.path,
    artifact?.url,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isSummaryArtifact(artifact) {
  const artifactType = String(artifact?.artifactType || "").toLowerCase();
  if (artifactType === "exec_summary") return true;

  const artifactText = getArtifactSearchText(artifact);
  return artifactText.includes("exec-summary") || artifactText.includes("exec summary");
}

function getSummaryArtifacts(htmlArtifacts) {
  return htmlArtifacts.filter(isSummaryArtifact);
}

function getDetailedArtifacts(htmlArtifacts, summaryArtifacts) {
  if (!summaryArtifacts.length) return htmlArtifacts;
  const summaryIds = new Set(summaryArtifacts.map((artifact) => artifact.id));
  return htmlArtifacts.filter((artifact) => !summaryIds.has(artifact.id));
}

function getDefaultSummaryArtifact(summaryArtifacts) {
  if (!summaryArtifacts.length) return null;

  // Primary flag is the canonical default when present.
  const primarySummary = summaryArtifacts.find(isPrimaryArtifact);
  if (primarySummary) return primarySummary;

  const nonBehavioralSummary = summaryArtifacts.find(
    (artifact) => !getArtifactSearchText(artifact).includes("behavioral")
  );
  if (nonBehavioralSummary) return nonBehavioralSummary;

  const primaryFlagSummary = summaryArtifacts.find(isPrimaryArtifact);
  return primaryFlagSummary || summaryArtifacts[0];
}

function getDefaultDetailedArtifact(detailedArtifacts) {
  if (!detailedArtifacts.length) return null;
  const primaryDetailed = detailedArtifacts.find(isPrimaryArtifact);
  return primaryDetailed || detailedArtifacts[0];
}

/**
 * Normalize analytics API payload into package(s) + navigable report tabs for one package.
 * JSON artifacts are preferred over HTML of the same type.
 *
 * @param {object|null|undefined} analyticsData
 * @param {string|null|undefined} selectedPackageId
 */
function emptyReportView(emptyState = "no_package") {
  return {
    reportPackages: [],
    reportPackage: null,
    selectedPackageId: null,
    reportArtifacts: [],
    htmlArtifacts: [],
    jsonArtifacts: [],
    viewerTabs: [],
    summaryArtifacts: [],
    detailedArtifacts: [],
    defaultSummaryArtifact: null,
    defaultDetailedArtifact: null,
    defaultArtifact: null,
    legacyFeedbackReportUrl: null,
    legacyFeedbackReportGeneratedAt: null,
    emptyState,
    hasRenderableReport: false,
    useLegacySingleReport: false,
  };
}

export function normalizeAnalyticsReportView(analyticsData, selectedPackageId = null) {
  if (!analyticsData) {
    return emptyReportView("no_package");
  }

  const reportPackages = getReportPackages(analyticsData);
  const effectivePackageId =
    selectedPackageId && reportPackages.some((pkg) => getPackageId(pkg) === String(selectedPackageId))
      ? String(selectedPackageId)
      : getDefaultPackageId(reportPackages);

  const reportPackage =
    reportPackages.find((pkg) => getPackageId(pkg) === effectivePackageId) ||
    reportPackages[0] ||
    analyticsData.reportPackage ||
    null;

  const rawArtifacts = analyticsData.reportArtifacts ?? analyticsData.artifacts ?? [];
  const allArtifacts = Array.isArray(rawArtifacts) ? sortArtifacts(rawArtifacts) : [];
  const reportArtifacts = filterArtifactsForPackage(allArtifacts, effectivePackageId);
  const mappedArtifacts = mapRenderableArtifacts(reportArtifacts);
  const htmlArtifacts = mappedArtifacts.filter(isHtmlArtifact);
  const jsonArtifacts = mappedArtifacts.filter(isJsonArtifact);
  const viewerTabs = buildViewerTabs(mappedArtifacts);
  const summaryArtifacts = getSummaryArtifacts(htmlArtifacts);
  const detailedArtifacts = getDetailedArtifacts(htmlArtifacts, summaryArtifacts);
  const defaultSummaryArtifact = getDefaultSummaryArtifact(summaryArtifacts);
  const defaultDetailedArtifact = getDefaultDetailedArtifact(detailedArtifacts);

  const primaryTab = viewerTabs.find((tab) => tab.isPrimary);
  const overviewTab = viewerTabs.find((tab) => tab.viewKind === "game_report");
  const defaultArtifact =
    overviewTab ||
    primaryTab ||
    viewerTabs[0] ||
    defaultSummaryArtifact ||
    defaultDetailedArtifact ||
    htmlArtifacts[0] ||
    jsonArtifacts[0] ||
    null;

  const legacyFeedbackReportUrl = getSafeReportUrl(analyticsData?.feedbackReport?.url);
  const legacyFeedbackReportGeneratedAt = analyticsData?.feedbackReport?.generatedAt || null;
  const useLegacySingleReport =
    reportPackages.length === 0 &&
    viewerTabs.length === 0 &&
    Boolean(legacyFeedbackReportUrl);

  const hasRenderableReport =
    viewerTabs.some((tab) => tab.safeUrl) || useLegacySingleReport;

  let emptyState = null;
  if (reportPackages.length === 0 && viewerTabs.length === 0 && !legacyFeedbackReportUrl) {
    emptyState = "no_package";
  } else if (reportPackage && viewerTabs.length === 0 && !legacyFeedbackReportUrl) {
    emptyState = "no_artifacts";
  } else if (!hasRenderableReport) {
    emptyState = "no_renderable_url";
  }

  return {
    reportPackages,
    reportPackage,
    selectedPackageId: effectivePackageId,
    reportArtifacts,
    htmlArtifacts,
    jsonArtifacts,
    viewerTabs,
    summaryArtifacts,
    detailedArtifacts,
    defaultSummaryArtifact,
    defaultDetailedArtifact,
    defaultArtifact,
    legacyFeedbackReportUrl,
    legacyFeedbackReportGeneratedAt,
    emptyState,
    hasRenderableReport,
    useLegacySingleReport,
  };
}

/**
 * @param {object[]} viewerTabs
 * @param {string|null|undefined} selectedArtifactId
 * @param {object|null} defaultArtifact
 * @returns {object|null}
 */
export function resolveSelectedArtifact(viewerTabs, selectedArtifactId, defaultArtifact) {
  if (!viewerTabs?.length) return null;

  if (selectedArtifactId) {
    const selectedId = String(selectedArtifactId);
    const exactMatch = viewerTabs.find((tab) => tab.id === selectedId);
    if (exactMatch) return exactMatch;

    const htmlMatch = viewerTabs.find((tab) => tab.htmlArtifactId === selectedId);
    if (htmlMatch) return htmlMatch;

    const jsonMatch = viewerTabs.find((tab) => tab.jsonArtifactId === selectedId);
    if (jsonMatch) return jsonMatch;
  }

  return defaultArtifact || viewerTabs[0] || null;
}
